import { sanitizeUrl } from '@/utils/sanitizeUrl';
import { type FC, useCallback, useMemo } from 'react';
import { Button } from '../common/Button';
import { SvgPrefixLink } from '../common/Icons/PrefixLink';
import { Modal } from '../common/Modal';
import { toastService } from '../common/Toaster';

import { getShareUrl, openShareWindow, socialLinks } from './configs';
import type { ShareModalProps, SharePlatform } from './types';

export const ShareModal: FC<ShareModalProps> = ({ isOpen, onClose, url }) => {
	const sanitizedUrl = useMemo(() => {
		if (url) {
			return sanitizeUrl(url);
		}
		if (typeof window !== 'undefined') {
			return sanitizeUrl(window.location.href);
		}
		return '';
	}, [url]);

	const copyToClipboard = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(sanitizedUrl);
			toastService.success('Copied to clipboard');
		} catch (_) {
			toastService.error('Failed to copy');
		}
	}, [sanitizedUrl]);

	const handleShare = useCallback(
		(platform: SharePlatform) => {
			const shareUrl = getShareUrl(platform, { url: sanitizedUrl });
			openShareWindow(shareUrl);
		},
		[sanitizedUrl],
	);

	const title = useMemo(
		() => (
			<h2 className="text-2xl leading-[130%] text-black font-[family-name:var(--font-manrope)]">
				Share Now
			</h2>
		),
		[],
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			showCloseIcon={true}
			size="lg"
		>
			{/* URL Input and Copy Button */}
			<div className="relative mb-5">
				<div className="absolute inset-y-0 start-0 flex items-center pointer-events-none z-20 ps-2.5">
					<SvgPrefixLink />
				</div>
				<input
					type="text"
					value={sanitizedUrl}
					readOnly
					title={sanitizedUrl}
					className="cursor-default w-full ps-[38px] px-2.5 pr-24 py-[14.5px] h-[50px] border border-[var(--color-grey-3)] rounded-[5px] font-manrope !truncate outline-0 focus:!truncate focus:ring-1 focus:ring-green"
				/>
				<Button
					onClick={copyToClipboard}
					variant="primary"
					fullWidth={false}
					className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-[5px] text-base"
				>
					Copy
				</Button>
			</div>

			{/* Share Text */}
			<div className="text-black mb-2.5 px-2.5 py-[7px] font-manrope">
				Share this link via
			</div>

			{/* Social Links */}
			<div className="flex gap-5">
				{Object.entries(socialLinks).map(([key, social]) => (
					<button
						key={key}
						onClick={() => handleShare(key as SharePlatform)}
						className="cursor-pointer"
						type="button"
					>
						<social.icon />
					</button>
				))}
			</div>
		</Modal>
	);
};
